import { OnInit, Input, ChangeDetectorRef, EventEmitter, Output, Directive } from '@angular/core';
import { FormControl, FormGroup, Validators, FormBuilder } from '@angular/forms';
import { Utils } from '../../../../../Utils';
import { PaymentService } from '../../service/payment.service';
import { ModalRouteService } from '../../service/modal-route.service';
import { IBankingInfo } from '../../interface/iBankingInfo';
import { IReceivedPaymentInfo } from '../../interface/iReceivedPaymentInfo';
import { ISendPaymentInfo } from '../../interface/iSendPaymentInfo';
import { AmorphicService } from '../../../../../../../coverpath-common/angular/services/amorphic/amorphic.service';
import { GiactUtils } from '../../../../../../../common/js/GiactUtils';
import { ValidationService } from '../../../../../../../coverpath-common/angular/services/validation/validation.service';
import _ from 'lodash';
import { ValidationErrorMessages } from '../../../../../../../coverpath-common/angular/classes/UnsupportedUseCases';

@Directive()
export class BankAccountBase implements OnInit {
  @Input() receivedPaymentInfo: IReceivedPaymentInfo;
  @Input() updatePaymentPayload: ISendPaymentInfo;
  @Output() updateComplete: EventEmitter<boolean> = new EventEmitter<boolean>();
  public paymentForm: FormGroup;
  public rnError: string = undefined;
  public anError: string = undefined;
  public policyNumber: string = '';
  public paymentInfo: IBankingInfo;
  public accountType: string;
  public giactWebDebitResponseCode: string;
  public isGiactWebDebitValid: boolean;
  public lastWebDebitAttemptCombination: string;
  public paymentInfoError: string;
  public isAccountRoutingInfoSame: boolean = false;

  public accountTypeOptions: any[] = [
    { value: 'personal_checking', description: 'Checking' },
    { value: 'personal_savings', description: 'Savings' },
    { value: 'business_checking', description: 'Business Checking' },
    { value: 'business_savings', description: 'Business Savings' },
  ];

  constructor(
    public paymentService: PaymentService,
    public fb: FormBuilder,
    private modalRouteService: ModalRouteService,
    private amorphicService: AmorphicService
  ) {}

  ngOnInit() {
    this.paymentService.resetGiactResponse();
    this.paymentForm = this.fb.group({
      accountType: ['', Validators.required],
      routingNumber: [
        '',
        [Validators.required, Validators.minLength(9), Validators.pattern('^[0-9]*$'), this.rnValidator.bind(this)],
      ],
      accountNumber: ['', [Validators.required, Validators.minLength(3), Validators.pattern('^[0-9]*$')]],
    });
  }

  rnValidator(control: FormControl) {
    const rn = control.value;
    if (!Utils.checkSum(rn)) {
      return {
        algorithm: true,
      };
    }
    return null;
  }

  isDisabled(): boolean {
    if (this.paymentForm.valid && this.isGiactWebDebitValid) {
      return false;
    }
    return true;
  }

  setAccountBusinessType() {
    const arr = this.paymentForm.get('accountType').value.split('_');
    this.updatePaymentPayload.payload.paymentMethod.accountType = <'checking' | 'savings'>arr[1];
    this.updatePaymentPayload.payload.paymentMethod.businessType = <'personal' | 'business'>arr[0];
  }

  updateBankInfo() {
    this.paymentService
      .updatePaymentInfo(
        this.paymentForm.get('routingNumber').value,
        this.paymentForm.get('accountNumber').value,
        this.paymentForm.get('accountType').value
      )
      .subscribe((res) => {
        this.updateComplete.emit(true);
      });
  }

  cancel() {
    this.paymentService.resetGiactResponse();
    this.updateComplete.emit(false);
  }

  //specifically for haven labels to match what was filed
  isNY(): boolean {
    if (this.amorphicService.controller.customer.primaryCustomer.address.state === 'NY') {
      return true;
    }

    return false;
  }

  async validateRN() {
    this.rnError = undefined;
    if (this.paymentForm.get('routingNumber').hasError('algorithm')) {
      this.rnError = 'Routing Number is not Correct!';
      return;
    }
    if (this.paymentForm.get('routingNumber').hasError('pattern')) {
      this.rnError = 'Routing Number has to be digits!';
      return;
    }
    if (this.paymentForm.get('routingNumber').hasError('minlength')) {
      this.rnError = 'Routing Number has to be 9 digits!';
      return;
    }
    if (this.paymentForm.get('routingNumber').hasError('required')) {
      this.rnError = 'Routing Number is Required.';
      return;
    }
    const paymentInfo = {
      paymentABA: this.paymentForm.get('routingNumber').value,
      paymentAccount: this.paymentForm.get('accountNumber').value,
    };
    await this.validatePaymentInfo(paymentInfo);
    this.rnError = undefined;
  }

  async validateAN() {
    this.anError = undefined;
    if (this.paymentForm.get('accountNumber').hasError('pattern')) {
      this.anError = 'Account Number has to be digits!';
      return;
    }
    if (this.paymentForm.get('accountNumber').hasError('required')) {
      this.anError = 'Account Number is Required.';
      return;
    }
    if (this.paymentForm.get('accountNumber').hasError('minlength')) {
      this.anError = 'Account number must be at least 3 digits.';
      return;
    }
    const paymentInfo = {
      paymentABA: this.paymentForm.get('routingNumber').value,
      paymentAccount: this.paymentForm.get('accountNumber').value,
    };
    await this.validatePaymentInfo(paymentInfo);
    this.anError = undefined;
  }

  async validatePaymentInfo(paymentInfo) {
    this.isAccountRoutingInfoSame = ValidationService.isAccountRoutingInfoSame(paymentInfo);
    if (_.isEmpty(paymentInfo.paymentAccount) || _.isEmpty(paymentInfo.paymentABA) || this.isAccountRoutingInfoSame) {
      return true;
    }
    const paymentInfoCombo = `${paymentInfo.paymentAccount}${paymentInfo.paymentABA}`;
    if (ValidationService.verifyIsNewPaymentCombination(paymentInfoCombo, this.lastWebDebitAttemptCombination)) {
      const giactResponse = await ValidationService.verifyWebDebit(paymentInfo);
      this.isGiactWebDebitValid = giactResponse.status === 'pass';
      this.giactWebDebitResponseCode = giactResponse.normalizedResponseCode;
      this.lastWebDebitAttemptCombination = paymentInfoCombo;
      this.isInvalidPaymentInfo();
      return giactResponse.status === 'pass';
    } else if (this.isGiactWebDebitValid) {
      return this.isGiactWebDebitValid;
    }
    return true;
  }

  isInvalidPaymentInfo() {
    this.paymentInfoError = this.getErrorMessage();
    return (
      this.isAccountRoutingInfoSame ||
      GiactUtils.isInvalidPayment(
        this.isGiactWebDebitValid,
        this.giactWebDebitResponseCode,
        this.amorphicService.controller.mmGiactWebDebitVerificationServiceEnabled
      )
    );
  }

  getErrorMessage() {
    return this.isAccountRoutingInfoSame
      ? ValidationErrorMessages.samePaymentInfoError
      : GiactUtils.getNormalizedGiactMessage(
          this.amorphicService.controller.mmGiactWebDebitVerificationServiceEnabled,
          this.giactWebDebitResponseCode
        );
  }
}
